import { createEntityAdapter, createSlice, type EntityState, type PayloadAction } from '@reduxjs/toolkit'

import { sharedHydrated } from '@/store/shared-actions'

import type { Asset, Extraction, ReviewTabId } from './types'

export const assetsAdapter = createEntityAdapter<Asset>()
/** Review state is keyed by project id. */
export const extractionsAdapter = createEntityAdapter<Extraction>()

export interface AssetsState {
  assets: EntityState<Asset, string>
  extractions: EntityState<Extraction, string>
}

const initialState: AssetsState = {
  assets: assetsAdapter.getInitialState(),
  extractions: extractionsAdapter.getInitialState(),
}

const pendingReviews = (): Extraction['reviews'] => ({
  assets: { status: 'pending', reviewedBy: null, reviewedAt: null },
  menuContent: { status: 'pending', reviewedBy: null, reviewedAt: null },
  creativeBrief: { status: 'pending', reviewedBy: null, reviewedAt: null },
})

export const assetsSlice = createSlice({
  name: 'assets',
  initialState,
  reducers: {
    assetsAdded(state, action: PayloadAction<Asset[]>) {
      assetsAdapter.addMany(state.assets, action.payload)
    },
    assetUpdated(state, action: PayloadAction<{ id: string; changes: Partial<Omit<Asset, 'id' | 'projectId'>> }>) {
      if (!state.assets.entities[action.payload.id]) return
      assetsAdapter.updateOne(state.assets, action.payload)
    },
    assetRemoved(state, action: PayloadAction<string>) {
      assetsAdapter.removeOne(state.assets, action.payload)
    },
    /** The server's three review flags for a project: a tab it says is done is reviewed here, one it says is not is pending. Who and when are kept when the status already agrees. */
    reviewsLoaded(state, action: PayloadAction<{ projectId: string; flags: Record<ReviewTabId, boolean> }>) {
      const { projectId, flags } = action.payload
      const extraction = state.extractions.entities[projectId] ?? { id: projectId, projectId, reviews: pendingReviews() }
      for (const tab of Object.keys(flags) as ReviewTabId[]) {
        const reviewed = extraction.reviews[tab].status === 'reviewed'
        if (flags[tab] && !reviewed) extraction.reviews[tab] = { status: 'reviewed', reviewedBy: null, reviewedAt: null }
        if (!flags[tab] && reviewed) extraction.reviews[tab] = { status: 'pending', reviewedBy: null, reviewedAt: null }
      }
      extractionsAdapter.setOne(state.extractions, extraction)
    },
    tabReviewSet(
      state,
      action: PayloadAction<{ projectId: string; tab: ReviewTabId; reviewed: boolean; by: string; at: string }>,
    ) {
      const { projectId, tab, reviewed, by, at } = action.payload
      const extraction = state.extractions.entities[projectId] ?? { id: projectId, projectId, reviews: pendingReviews() }
      extraction.reviews[tab] = reviewed ? { status: 'reviewed', reviewedBy: by, reviewedAt: at } : { status: 'pending', reviewedBy: null, reviewedAt: null }
      extractionsAdapter.setOne(state.extractions, extraction)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sharedHydrated, (_state, action) => action.payload.data.assets)
  },
})

export const { assetsAdded, assetUpdated, assetRemoved, reviewsLoaded, tabReviewSet } = assetsSlice.actions
